import React, { useEffect, useMemo, useState } from 'react';
import { Group, ActionIcon, ColorSwatch, Stack, Box, useMantineTheme, Modal, Button, Text, UnstyledButton, ColorPicker, TextInput } from '@mantine/core';
import { IconArrowBackUp, IconArrowForwardUp, IconEraser, IconTrash } from '@tabler/icons-react';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import { DrawingTool } from '../utils/drawingExport';

interface DrawingControlsProps {
  currentColor: string;
  currentTool?: DrawingTool;
  currentSize: number;
  onColorChange: (color: string) => void;
  onSizeChange: (size: number) => void;
  onToolChange?: (tool: DrawingTool) => void;
  onClear: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  canClear?: boolean;
  hideClear?: boolean;
  showEraser?: boolean;
}

const COLORS = [
  '#000000', // Black
  '#FF0000', // Red
  '#00FF00', // Green
  '#0000FF', // Blue
  '#FFFF00', // Yellow
  '#FF00FF', // Magenta
  '#00FFFF', // Cyan
  '#FFFFFF', // White
];

const SIZES = [4, 10, 20, 40, 200];
const MOBILE_SIZES = [6, 16, 30, 150];
const RECENT_COLORS_KEY = 'drawww_recent_colors';

// Rendered alongside the drawing surface, which re-renders on every poll
// refetch; memoized so identical props skip the controls subtree
export const DrawingControlsMantine = React.memo(function DrawingControlsMantine({
  currentColor,
  currentTool = 'pen',
  currentSize,
  onColorChange,
  onSizeChange,
  onToolChange,
  onClear,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  canClear,
  hideClear = false,
  showEraser = false,
}: DrawingControlsProps) {
  const { isMobile } = useResponsiveLayout();
  const theme = useMantineTheme();
  const [showClearModal, setShowClearModal] = useState(false);
  const [showCustomColorModal, setShowCustomColorModal] = useState(false);
  const [customColor, setCustomColor] = useState(currentColor);
  const [recentColors, setRecentColors] = useState<string[]>([]);
  
  const handleClear = () => {
    setShowClearModal(false);
    onClear();
  };

  const showUndoRedo = Boolean(onUndo || onRedo);

  // Single line layout for both mobile and desktop
  const colors = showEraser ? COLORS.filter((color) => color !== '#FFFFFF') : COLORS;
  const sizes = isMobile ? MOBILE_SIZES : SIZES;
  const displayColor = currentTool === 'eraser' ? '#ffffff' : currentColor;
  const isPaletteColor = colors.includes(currentColor);
  const isCustomActive = currentTool !== 'eraser' && !isPaletteColor;
  const swatchSize = isMobile ? 24 : 36;
  const isClearDisabled = canClear === false;
  const sanitizedCustomColor = useMemo(() => {
    const value = customColor.trim();
    if (!value) return '';
    return value.startsWith('#') ? value : `#${value}`;
  }, [customColor]);
  const isCustomColorValid = /^#(?:[0-9a-fA-F]{3}){1,2}$/.test(sanitizedCustomColor);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_COLORS_KEY);
      if (!stored) return;
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        setRecentColors(parsed.filter((color) => /^#(?:[0-9a-fA-F]{3}){1,2}$/.test(color)));
      }
    } catch {
      // Ignore invalid storage.
    }
  }, []);

  useEffect(() => {
    if (currentTool !== 'eraser' && !isPaletteColor) {
      setCustomColor(currentColor);
    }
  }, [currentColor, currentTool, isPaletteColor]);
  
  return (
    <>
      <Group gap={isMobile ? "xs" : "md"} justify="center" wrap="nowrap">
        {showUndoRedo && (
          <>
            <ActionIcon
              variant="light"
              size={isMobile ? "md" : "lg"}
              onClick={onUndo}
              disabled={!onUndo || !canUndo}
              aria-label="Undo"
            >
              <IconArrowBackUp size={isMobile ? 18 : 20} />
            </ActionIcon>
            <ActionIcon
              variant="light"
              size={isMobile ? "md" : "lg"}
              onClick={onRedo}
              disabled={!onRedo || !canRedo}
              aria-label="Redo"
            >
              <IconArrowForwardUp size={isMobile ? 18 : 20} />
            </ActionIcon>
            <Box style={{ 
              width: 1, 
              height: swatchSize, 
              backgroundColor: 'var(--mantine-color-gray-3)',
              flexShrink: 0
            }} />
          </>
        )}

        {/* Colors */}
        {colors.map((color) => (
          <ColorSwatch
            key={color}
            color={color}
            size={swatchSize}
            radius="sm"
            style={{ 
              cursor: 'pointer',
              border: currentColor === color && currentTool !== 'eraser'
                ? `2px solid ${theme.colors.blue[6]}`
                : '1px solid var(--mantine-color-gray-4)',
              boxShadow: currentColor === color && currentTool !== 'eraser' ? theme.shadows.sm : 'none',
              flexShrink: 0
            }}
            onClick={() => {
              onColorChange(color);
              onToolChange?.('pen');
            }}
          />
        ))}
        <ColorSwatch
          color="transparent"
          size={swatchSize}
          radius="sm"
          title="Pick any color"
          style={{
            cursor: 'pointer',
            border: isCustomActive
              ? `2px solid ${theme.colors.blue[6]}`
              : '1px solid var(--mantine-color-gray-4)',
            boxShadow: isCustomActive ? theme.shadows.sm : 'none',
            backgroundImage: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 25%, #48dbfb 50%, #1dd1a1 75%, #5f27cd 100%)',
            backgroundColor: 'transparent',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={() => setShowCustomColorModal(true)}
        >
          <Box
            style={{
              width: swatchSize * 0.45,
              height: swatchSize * 0.45,
              borderRadius: '50%',
              backgroundColor: customColor,
              border: customColor.toLowerCase() === '#ffffff'
                ? '1px solid var(--mantine-color-gray-4)'
                : '1px solid rgba(0, 0, 0, 0.15)'
            }}
          />
        </ColorSwatch>
        
        {/* Divider */}
        <Box style={{ 
          width: 1, 
          height: swatchSize, 
          backgroundColor: 'var(--mantine-color-gray-3)',
          flexShrink: 0
        }} />
        
        {/* Brush sizes */}
        {sizes.map((size) => {
          const maxPreviewSize = isMobile ? 28 : 40;
          const previewSize = Math.min(size, maxPreviewSize);
          return (
          <UnstyledButton
            key={size}
            onClick={() => onSizeChange(size)}
            style={{
              width: isMobile ? 28 : 40,
              height: isMobile ? 28 : 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: theme.radius.sm,
              border: currentSize === size ? `2px solid ${theme.colors.blue[6]}` : '1px solid transparent',
              backgroundColor: currentSize === size ? 'var(--mantine-color-gray-0)' : 'transparent',
              transition: 'all 0.2s ease',
              flexShrink: 0
            }}
          >
            <Box
              style={{
                width: previewSize,
                height: previewSize,
                borderRadius: '50%',
                backgroundColor: displayColor,
                border: displayColor === '#ffffff' ? '1px solid var(--mantine-color-gray-4)' : undefined,
              }}
            />
          </UnstyledButton>
          );
        })}

        {showEraser && onToolChange && (
          <>
            <Box style={{ 
              width: 1, 
              height: isMobile ? 24 : 36, 
              backgroundColor: 'var(--mantine-color-gray-3)',
              flexShrink: 0
            }} />
            <ActionIcon
              variant={currentTool === 'eraser' ? 'filled' : 'light'}
              size={isMobile ? "md" : "lg"}
              onClick={() => onToolChange(currentTool === 'eraser' ? 'pen' : 'eraser')}
              color={currentTool === 'eraser' ? 'teal' : 'gray'}
              aria-label="Toggle eraser"
            >
              <IconEraser size={isMobile ? 18 : 20} />
            </ActionIcon>
          </>
        )}
        
        {/* Clear button - only show if not hidden */}
        {!hideClear && (
          <>
            {/* Divider */}
            <Box style={{ 
              width: 1, 
              height: isMobile ? 24 : 36, 
              backgroundColor: 'var(--mantine-color-gray-3)',
              flexShrink: 0
            }} />
            
            <ActionIcon
              variant="light"
              size={isMobile ? "md" : "lg"}
              onClick={() => setShowClearModal(true)}
              color="red"
              aria-label="Clear canvas"
              disabled={isClearDisabled}
            >
              <IconTrash size={isMobile ? 18 : 20} />
            </ActionIcon>
          </>
        )}
      </Group>

      {/* Clear confirmation modal */}
      <Modal
        opened={showClearModal}
        onClose={() => setShowClearModal(false)}
        title="Clear Drawing?"
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text>Are you sure you want to clear your drawing? This cannot be undone.</Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="light"
              onClick={() => setShowClearModal(false)}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleClear}
            >
              Clear Drawing
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={showCustomColorModal}
        onClose={() => setShowCustomColorModal(false)}
        title="Pick any color"
        centered
        size="sm"
      >
        <Stack gap="md">
          <ColorPicker
            value={isCustomColorValid ? sanitizedCustomColor : currentColor}
            onChange={setCustomColor}
            format="hex"
            fullWidth
          />
          {recentColors.length > 0 && (
            <Stack gap={6}>
              <Text size="sm" fw={600}>
                Recent colors
              </Text>
              <Group gap="xs">
                {recentColors.map((color) => {
                  const isSelected = color.toLowerCase() === sanitizedCustomColor.toLowerCase();
                  return (
                    <ColorSwatch
                      key={color}
                      color={color}
                      size={isMobile ? 22 : 28}
                      radius="sm"
                      style={{
                        cursor: 'pointer',
                        border: isSelected ? `2px solid ${theme.colors.blue[6]}` : '1px solid var(--mantine-color-gray-4)',
                        boxShadow: isSelected ? theme.shadows.sm : 'none',
                      }}
                      onClick={() => setCustomColor(color)}
                    />
                  );
                })}
              </Group>
            </Stack>
          )}
          <TextInput
            label="Hex color"
            value={sanitizedCustomColor}
            placeholder="#FF5DB1"
            onChange={(event) => {
              const value = event.currentTarget.value;
              setCustomColor(value ? (value.startsWith('#') ? value : `#${value}`) : '');
            }}
          />
          <Group justify="flex-end" gap="sm">
            <Button
              variant="light"
              onClick={() => setShowCustomColorModal(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={!isCustomColorValid}
              onClick={() => {
                const nextColor = sanitizedCustomColor || currentColor;
                onColorChange(nextColor);
                onToolChange?.('pen');
                setRecentColors((prev) => {
                  const normalized = nextColor.toLowerCase();
                  const next = [normalized, ...prev.filter((color) => color.toLowerCase() !== normalized)].slice(0, 10);
                  try {
                    localStorage.setItem(RECENT_COLORS_KEY, JSON.stringify(next));
                  } catch {
                    // Ignore storage errors.
                  }
                  return next;
                });
                setShowCustomColorModal(false);
              }}
            >
              Use color
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
});
